import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { batch, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Badge,
  Button,
  ButtonSelect,
  Icon,
  InlineMessage,
  InputField,
  Link as DSLink,
  MenuMulti,
  type MenuMultiOption,
  Typography,
} from '@saviynt/design-system';

import {
  CF_NEU_STL,
  CHAR_LIMIT_FOR_SEARCH,
  F_KEY_ON,
  F_KEY_ST,
  l,
  type MessageState,
  SERVICE_IDENTITY_LIST_CREATE,
} from '../../../../shared/constants';
import {
  useDebounce,
  useDebouncedFn,
  useTypedSelector,
} from '../../../../shared/hooks';
import { serviceIdentityActions } from '../../data-store/serviceIdentitySlice';
import { useLoadFilterOptions } from '../../hooks';
import { getNoDataText } from '../../utils';

import './ServiceIdentityFilter.css';

/**
 * Search and filter bar for the Service Identity list. Renders a debounced
 * search input, an "Add Service Identity" button, and a collapsible
 * multi-select filter panel. Filter controls are disabled while global
 * loading is active.
 * @returns The search/filter bar with create button and conditional filter panel.
 */
function ServiceIdentityFilter() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    filters,
    activeFilters,
    isFilterBar,
    isGlobalLoading,
    list: { totalCount },
  } = useTypedSelector((state) => state.savicopilot.serviceIdentity);

  // States
  const [searchValue, setSearchValue] = useState('');

  // Constants
  const isBadgeIcon = useMemo(
    () => filters.some((f) => f.options.some((o) => o.isChecked)),
    [filters]
  );
  const isClrDisabled = useMemo(
    () => filters.some((f) => f.isLoading),
    [filters]
  );

  // Hooks
  const { loadFilterOptions } = useLoadFilterOptions();
  const debouncedSearchValue = useDebounce<string>(searchValue, 300);

  // Utils
  const debouncedLoadFilterOptions = useDebouncedFn(loadFilterOptions, 300);

  const handleFilterChange = useCallback(
    (fk: string, so: MenuMultiOption[]) =>
      batch(() => {
        dispatch(
          serviceIdentityActions.setFilters(
            filters.map((f) =>
              f.value === fk
                ? {
                    ...f,
                    options: f.options.map((o) => ({
                      ...o,
                      isChecked: so.some((_o) => _o.value === o.value),
                    })),
                  }
                : f
            )
          )
        );
        dispatch(
          serviceIdentityActions.setActiveFilters({
            ...activeFilters,
            [fk]: (so as (MenuMultiOption & { type: string })[]).map((_o) => {
              if (fk === F_KEY_ON) {
                return {
                  id: Number(_o.value),
                  type: _o.type,
                };
              }

              return _o.value;
            }),
          })
        );
      }),
    [dispatch, activeFilters, filters]
  );

  const handleFooterLink2Click = useCallback(
    (fk: string) =>
      batch(() => {
        dispatch(
          serviceIdentityActions.setFilters(
            filters.map((f) =>
              f.value === fk
                ? {
                    ...f,
                    options: f.options.map((o) => ({
                      ...o,
                      isChecked: false,
                    })),
                  }
                : f
            )
          )
        );
        dispatch(
          serviceIdentityActions.setActiveFilters({
            ...activeFilters,
            [fk]: [],
          })
        );
      }),
    [dispatch, activeFilters, filters]
  );

  const handleClearBtn = useCallback(
    () =>
      batch(() => {
        dispatch(
          serviceIdentityActions.setFilters(
            filters.map((f) => ({
              ...f,
              options: f.options.map((o) => ({
                ...o,
                isChecked: false,
              })),
            }))
          )
        );
        dispatch(serviceIdentityActions.setActiveFilters({}));
      }),
    [dispatch, filters]
  );

  const handleScroll = (e: React.UIEvent<HTMLUListElement>, fk: string) => {
    const target = e.target as HTMLUListElement;
    const { scrollTop, scrollHeight, clientHeight } = target;

    if (scrollHeight - scrollTop === clientHeight) {
      debouncedLoadFilterOptions(fk);
    }
  };

  // Effects
  useEffect(() => {
    if (
      debouncedSearchValue.trim().length <= CHAR_LIMIT_FOR_SEARCH &&
      debouncedSearchValue.trim().length > 0
    ) {
      return;
    }

    dispatch(serviceIdentityActions.setSearchText(debouncedSearchValue));
  }, [debouncedSearchValue, dispatch]);

  useEffect(() => {
    filters.forEach((f) => {
      if (f.isApiBased) {
        loadFilterOptions(f.value, true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- want to run one time only
  }, []);

  return (
    <div className='ServiceIdentityFilter'>
      <div className='ServiceIdentityFilter-searchColumn'>
        <div className='ServiceIdentityFilter-searchRow'>
          <div className='ServiceIdentityFilter-searchInput'>
            <InputField
              id='agent-search-input'
              name='search'
              size='medium'
              placeholder={intl.formatMessage({
                id: 'qkJYSY',
                defaultMessage: 'Search by name, description, or tags',
                description:
                  'Placeholder text for input field in TypedChipInput component',
              })}
              kind='search'
              value={searchValue}
              setValue={setSearchValue}
              prefixIcon={
                <Icon kind='Search' size='small' color={CF_NEU_STL} />
              }
              isCritical={
                searchValue.trim().length <= CHAR_LIMIT_FOR_SEARCH &&
                searchValue.trim().length > 0
              }
              CriticalHelperText={
                <InlineMessage
                  text={intl.formatMessage({
                    id: 'rMplSY',
                    defaultMessage:
                      'Type more than 2 characters to see results.',
                    description: 'Type more than 2 characters to see results.',
                  })}
                  colorTheme='critical'
                  size='small'
                  leftIcon={<Icon kind='AlertCriticalFilled' />}
                />
              }
            />
          </div>
          <ButtonSelect
            label={intl.formatMessage({
              id: 'vYViQu',
              defaultMessage: 'Filter',
              description: 'Filter',
            })}
            onClick={() => {
              dispatch(serviceIdentityActions.setIsFilterBarOpen(!isFilterBar));
            }}
            isOpen={isFilterBar}
            isDisabled={isGlobalLoading}
            size='medium'
            prefixIcon={<Icon kind='Filter' />}
            BadgeComp={
              isBadgeIcon ? (
                <Badge kind='dot' color='primary' size='small' />
              ) : undefined
            }
          />
          <Button
            kind='outlined'
            leftIcon={<Icon kind='Add' />}
            onClick={() => history.push(SERVICE_IDENTITY_LIST_CREATE)}
            className='ServiceIdentityFilter-createNewBtn'>
            <FormattedMessage
              id='nSa1mG'
              defaultMessage='Add Service Identity'
              description='Add Service Identity button text'
            />
          </Button>
        </div>
      </div>
      {isFilterBar && (
        <div className='ServiceIdentityFilter-menuWrapper'>
          {filters.map((f) => {
            const noDataText = getNoDataText(
              f.isError,
              f.isLoading,
              f.searchText,
              f.options.length
            );

            return (
              <MenuMulti
                key={f.value}
                label={intl.formatMessage(l[f.label as MessageState])}
                options={
                  f.value !== F_KEY_ST
                    ? f.options
                    : f.options.map((o) => ({
                        ...o,
                        label: intl.formatMessage(l[o.label as MessageState]),
                      }))
                }
                trigger={
                  <ButtonSelect
                    kind='pill'
                    label=''
                    isDisabled={f.isLoading ?? isGlobalLoading}
                  />
                }
                onChange={(so) => {
                  handleFilterChange(f.value, so ?? []);
                }}
                heightToShowScrollbar='16rem'
                minOptionsForFooter={6}
                minOptionsForSearch={f.isApiBased ? 0 : f.options.length + 1}
                noDataText={
                  noDataText ? intl.formatMessage(l[noDataText]) : undefined
                }
                onScroll={(e) => handleScroll(e, f.value)}
                footerLink1Text=' '
                footerLink2Text={intl.formatMessage({
                  id: 'sOTYaP',
                  defaultMessage: 'Clear Selection',
                  description: 'Clear Selection',
                })}
                onFooterLink2Click={() => handleFooterLink2Click(f.value)}
                isFilteringDisabled
                showOptionLabelOnHover
                isLoading={f.isLoading}
                hasMoreOptions={f.hasMoreOptions}
                loadingText={intl.formatMessage({
                  id: 'KmuT+3',
                  defaultMessage: 'Loading...',
                  description: 'Loading',
                })}
                onSearchTermChange={(v: string | null) => {
                  const searchText = v ?? '';

                  dispatch(
                    serviceIdentityActions.setFilters(
                      filters.map((_f) =>
                        _f.value === f.value ? { ..._f, searchText } : _f
                      )
                    )
                  );

                  if (f.isApiBased) {
                    debouncedLoadFilterOptions(f.value, true, searchText);
                  }
                }}
              />
            );
          })}
          {isBadgeIcon && (
            <DSLink
              text={intl.formatMessage({
                id: 'e9OvzW',
                defaultMessage: 'Clear',
                description: 'Clear',
              })}
              kind='button'
              onClick={handleClearBtn}
              isDisabled={isClrDisabled || isGlobalLoading}
            />
          )}
        </div>
      )}
      {totalCount !== null && totalCount > 1 && (
        <Typography
          kind='body3'
          color={CF_NEU_STL}
          className='ServiceIdentityFilter-itemCount'>
          <FormattedMessage
            id='ec5lTS'
            defaultMessage='{count} items'
            description='Service Identity list count'
            values={{ count: totalCount }}
          />
        </Typography>
      )}
    </div>
  );
}

export default memo(ServiceIdentityFilter);
